/**
 * Who may see a Sales Coach review for one appointment.
 *
 * Providers see only reviews for appointments they ran — booked or supervising. Business
 * owners and admins see every provider's. That is `ADMIN_LEVEL_GROUPS` (Business owners,
 * Administrators, Suno), which `useUserRoles().isAdminOrBusinessOwner` already computes.
 *
 * ⚠️ **This gate is client-side only.** Neither `/coach/` viewset is role-scoped — both ship
 * behind plain `IsPracticeStaffUser`, and `sales-coach/CLAUDE.md` still lists a role gate under
 * *Not built*. It hides the surfaces; it does not stop a provider reading another provider's
 * review through the API. The server-side gate is tracked as the follow-up in
 * `docs/ambient-scribes/sales-coach/SN-19586-BACKEND-REQUIREMENTS.md`.
 *
 * ⚠️ **This file must keep importing NOTHING.** Both host chunks (patients, medical-scribe)
 * import it statically so they can decide without pulling the lazily-loaded Sales Coach section
 * in behind it — the same constraint, for the same reason, that
 * `hooks/use-sales-coach-route-access.hook.ts` documents. That is also why the pre-auth user is
 * recognised by its value rather than by importing `PRE_AUTH_USER_ID` from `auth-slice`.
 */
export interface SalesCoachReviewAccessArgs {
  /** `useUserRoles().isAdminOrBusinessOwner` — Business owners, Administrators, Suno. */
  isAdminOrBusinessOwner: boolean;
  /** `useUserRoles().userId`. */
  userId: number | undefined;
  /** The appointment's booked clinician (`OpportunityAppointment.provider_id`). */
  providerId: number | null | undefined;
  /** The appointment's direct supervising provider, where one is known. */
  supervisingProviderId?: number | null;
}

/**
 * `PRE_AUTH_USER_ID` is `-1`, and it is TRUTHY — the placeholder user that
 * `/auth/users/me/` has not replaced yet. A `userId ? …` guard sails straight past it, which is
 * how several guards in this repo came to answer before auth had resolved. Real ids are
 * positive, so anything at or below zero is "we do not know who this is".
 */
const isResolvedUser = (userId: number | undefined): userId is number =>
  typeof userId === "number" && userId > 0;

export function canViewSalesCoachReview({
  isAdminOrBusinessOwner,
  userId,
  providerId,
  supervisingProviderId
}: SalesCoachReviewAccessArgs): boolean {
  if (isAdminOrBusinessOwner) return true;
  if (!isResolvedUser(userId)) return false;
  return providerId === userId || supervisingProviderId === userId;
}
